import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Drop, Moon, Brain, Shower, FirstAid, ListChecks, Leaf } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const getSectionAnchorId = (guideId, index) => `post-party-${guideId}-section-${index}`;

const PostPartySelfCarePage = () => {
  const navigate = useNavigate();
  const [selectedGuide, setSelectedGuide] = useState(null);

  const guides = [
    {
      id: 'general',
      emoji: '💜',
      title: 'Post-Party Self-Care',
      subtitle: 'Recovery basics for everyone',
      icon: Heart,
      color: 'from-purple-500 to-pink-500',
      content: {
        intro: "After partying, your body and mind need care. Substance use, sleep loss, and intense experiences can leave you dehydrated, exhausted, or emotionally drained. Taking time for self-care helps you recover faster.",
        sections: [
          {
            title: 'Aftercare & Grounding',
            icon: '🫂',
            items: [
              'Aftercare is what you do after the party to take care of your body and feelings.',
              'This can include water + food, sleep, shower, checking in with yourself, and checking in with someone you trust.',
              'Aftercare is part of harm reduction.'
            ]
          },
          {
            title: 'Hydrate First',
            icon: '💧',
            items: [
              'Drink water slowly but steadily',
              'Electrolytes (sports drinks or rehydration solutions) help replace what your body lost',
              'Keep water bottles around your recovery space'
            ]
          },
          {
            title: 'Eat Something Nourishing',
            icon: '🍌',
            items: [
              'Your body needs fuel to recover',
              'Simple options: fruit, eggs, smoothies, toast with peanut butter',
              'Something salty helps replace electrolytes',
              'Avoid overloading on junk food - focus on nutrients'
            ]
          },
          {
            title: 'Rest & Sleep',
            icon: '😴',
            items: [
              'Sleep helps your brain repair from stress and substance use',
              'Short naps are okay if you cannot sleep fully',
              'Melatonin can help regulate sleep if needed'
            ]
          },
          {
            title: 'Mind Your Mental Health',
            icon: '🧠',
            items: [
              'Check in with yourself: anxiety, low mood, or racing thoughts are common after partying',
              'Journaling, deep breathing, or grounding exercises can help',
              'Talk to a trusted peer or community member if you feel off'
            ]
          },
          {
            title: 'Gentle Body Care',
            icon: '🛁',
            items: [
              'Stretch or move lightly to ease muscle tension',
              'Shower or wash your face - feeling clean helps your mind reset',
              'Massage hands, neck, or shoulders if tense'
            ]
          },
          {
            title: 'Monitor Your Health',
            icon: '🏥',
            items: [
              'Track any comedowns or unusual reactions',
              'Seek medical attention if you feel extremely unwell',
              'If you used multiple substances, check for interactions'
            ]
          },
          {
            title: 'A Reminder: RAPS',
            icon: '🥤',
            items: [
              'Basic physical aftercare is similar to the requirements after giving blood.',
              'A commonly used acronym to remember the important elements is RAPS: Rest, Aqua, Protein, Sugar.',
              'Rest: quiet time, reflection, conversation with partner, cuddling, and similar gentle care.',
              'Aqua: water, hydration, electrolytes.',
              'Protein: meat, nuts, cheese, and other real fuel.',
              'Sugar: fruit, juice, chocolate, and easy energy.'
            ]
          },
          {
            title: 'Ground Yourself',
            icon: '🌿',
            items: [
              'Grounding helps you come back to your body and the present moment.',
              'Slow breathing.',
              'Holding something warm.',
              'Touching something real.',
              'Remind yourself: “I’m here, I’m safe, this will pass.”'
            ]
          },
          {
            title: 'Knowing When to Step Back',
            icon: '⏸️',
            items: [
              'Sometimes the safest choice is a pause — not forever, just a step back.',
              'Signs you might need a break: partying feels like obligation, not choice.',
              'Signs you might need a break: you feel worse after than before.',
              'Signs you might need a break: you’re using to escape pain all the time.',
              'Signs you might need a break: your body is asking for rest.',
              'Stepping back is not failure. It’s listening to yourself.'
            ]
          },
          {
            title: 'Anxiety, Depression & Comedowns',
            icon: '🌧️',
            items: [
              'Comedowns can hit mental health hard: anxiety, sadness, shame, emptiness, and racing thoughts.',
              'These feelings are common and don’t mean something is “wrong” with you.',
              'Your brain chemistry is shifting.',
              'PSP normalizes comedowns and encourages planning for aftercare.',
              'PSP normalizes comedowns and encourages not making big life decisions while crashing.',
              'PSP normalizes comedowns and encourages reaching out instead of isolating.'
            ]
          },
          {
            title: 'Trauma & Minority Stress',
            icon: '💙',
            items: [
              'Many people in PnP spaces carry past trauma, rejection, stigma, homophobia, racism, HIV stigma, and minority stress from living in a world that doesn’t always feel safe.',
              'Partying can sometimes feel like relief from that pressure.',
              'But those stressors don’t disappear after the party ends.',
              'We hold space for the reality that people use substances and sex to cope and still deserve compassion and support each other.'
            ]
          },
          {
            title: 'What Support Can Look Like',
            icon: '🤝',
            items: [
              'Support doesn’t mean fixing people.',
              'It means listening without judging.',
              'Checking in.',
              'Believing people’s feelings.',
              'Offering options, not pressure.'
            ]
          }
        ],
        checklist: [
          'Water & electrolytes',
          'Nutritious food',
          'Rest or sleep',
          'Grounding / mental check-in',
          'Gentle movement & hygiene',
          'Monitor for health concerns',
          'RAPS: Rest, Aqua, Protein, Sugar',
          'Reach out instead of isolating if the comedown gets heavy'
        ],
        footer: "Recovery isn't weak - it's smart. Every step you take now protects your body, mind, and community, and aftercare is part of harm reduction."
      }
    },
    {
      id: 'tina',
      emoji: '💨',
      title: 'Tina Self-Care Guide',
      subtitle: 'Post-smoke recovery edition',
      icon: Leaf,
      color: 'from-cyan-500 to-blue-500',
      content: {
        intro: "After smoking Tina, many of us feel that dry cough, stuck mucus, white phlegm, sore throat, or just plain rough. This is your go-to self-care list because harm reduction = self-love.",
        sections: [
          {
            title: 'Step 1: Clean Your Mouth + Throat',
            icon: '🦷',
            items: [
              'Brush your teeth - clear out meth residue & bacteria',
              'Scrape your tongue - gently remove white film',
              'Gargle with warm salt water - mix ½ tsp salt in warm water, swish it around',
              'Spit out the mucus - don\'t swallow it if it feels nasty'
            ]
          },
          {
            title: 'Step 2: Open Up Your Lungs & Nose',
            icon: '🫁',
            items: [
              'Hot shower or steam bowl - helps loosen phlegm and clear sinuses',
              'Breathe in steam - bowl of hot water + towel over your head (5-10 min)',
              'Use saline spray or neti pot - clears nose if clogged'
            ]
          },
          {
            title: 'Step 3: Soothe That Burn',
            icon: '🍵',
            items: [
              'It\'s tea time, babe: Ginger + lemon + honey (heals + tastes cute)',
              'Or chamomile, licorice root, or marshmallow root tea',
              'Skip cold drinks - go room temp or warm',
              'If lips are dry, use lip balm (we know the Tina lips struggle is real)'
            ]
          },
          {
            title: 'Step 4: Kick Out the Mucus',
            icon: '💊',
            items: [
              'Guaifenesin (like Robitussin Mucus & Phlegm) - helps clear your chest',
              'NAC (N-acetylcysteine) - detoxes lungs and breaks down gunk',
              'Vitamin C - 500-1000mg helps your immune system reset',
              'Magnesium - 200-400mg to relax muscles + ease anxiety'
            ]
          },
          {
            title: 'Step 5: Rehydrate Like a Boss',
            icon: '💧',
            items: [
              'Drink 2-3 glasses of water',
              '1 glass of electrolyte drink (coconut water, Gatorade)',
              'DIY electrolytes: water + pinch of salt + lemon + honey',
              'Snack on: banana, toast, soup, smoothie, or avocado - something easy on your tummy but real'
            ]
          },
          {
            title: 'Step 6: Rest & Reset',
            icon: '🛏️',
            items: [
              'Lay down with your head slightly up (helps mucus drain)',
              'Play chill music or comfort TV',
              'Try to sleep - sleeping lets your body reboot and heal'
            ]
          }
        ],
        footer: "You deserve care, not shame. Whether you partied to feel sexy, numb pain, escape, or just connect - your health still matters. There's no judgment here. Only love, awareness, and safer ways to do what you do."
      }
    }
  ];

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectedGuide ? setSelectedGuide(null) : navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">🌙</span>
              <h1 className="text-2xl font-bold text-gradient">
                {selectedGuide ? guides.find(g => g.id === selectedGuide)?.title : 'Grounding & Self-Care'}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        {!selectedGuide ? (
          <>
            {/* Hero */}
            <div className="mb-8 p-6 party-gradient rounded-3xl text-white text-center">
              <div className="text-5xl mb-3">✨</div>
              <h2 className="text-2xl font-bold mb-3">Party hard, land soft 🌙</h2>
              <p className="text-white/90 leading-relaxed">
                Your body and mind need care after partying. These guides help you recover faster and stay grounded. No shame, just love. 💜
              </p>
            </div>

            {/* Guide Cards */}
            <div className="space-y-4">
              {guides.map((guide) => {
                const Icon = guide.icon;
                return (
                  <button
                    key={guide.id}
                    onClick={() => setSelectedGuide(guide.id)}
                    data-testid={`guide-${guide.id}`}
                    className="w-full group relative bg-[rgb(var(--surface))] hover:bg-[rgb(var(--surface-elevated))] rounded-3xl p-6 border border-white/10 text-left transition-all duration-200 hover:border-[rgb(var(--primary))]/50 btn-hover overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${guide.color} opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                    <div className="relative z-10 flex items-start">
                      <div className="text-5xl mr-4 emoji-bounce">{guide.emoji}</div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[rgb(var(--text-primary))] mb-1">
                          {guide.title}
                        </h3>
                        <p className="text-sm text-[rgb(var(--text-secondary))]">
                          {guide.subtitle}
                        </p>
                      </div>
                      <Icon size={24} weight="duotone" className="text-[rgb(var(--primary))] group-hover:scale-110 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Quick reminder */}
            <div className="mt-8 p-4 bg-[rgb(var(--danger))]/10 border border-[rgb(var(--danger))]/20 rounded-2xl">
              <p className="text-sm text-[rgb(var(--text-secondary))] text-center">
                <strong className="text-[rgb(var(--danger))]">Seek help if:</strong> Trouble breathing, blue lips, unconsciousness, seizures, or extreme confusion. Calling for help saves lives.
              </p>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            {guides.find(g => g.id === selectedGuide)?.content && (() => {
              const guide = guides.find(g => g.id === selectedGuide);
              const content = guide.content;
              
              return (
                <>
                  {/* Intro */}
                  <div className={`p-6 bg-gradient-to-br ${guide.color} rounded-3xl text-white`} data-testid={`post-party-guide-intro-${guide.id}`}>
                    <p className="leading-relaxed">
                      {content.intro}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-[rgb(var(--surface))] p-4" data-testid={`post-party-guide-quick-jump-${guide.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-[rgb(var(--text-secondary))]">
                        Quick jump
                      </h3>
                      <p className="text-xs text-[rgb(var(--text-secondary))]">
                        Tap a section to scan faster on mobile
                      </p>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {content.sections.map((section, idx) => (
                        <a
                          key={`${guide.id}-jump-${idx}`}
                          href={`#${getSectionAnchorId(guide.id, idx)}`}
                          data-testid={`post-party-guide-jump-link-${guide.id}-${idx}`}
                          className="whitespace-nowrap rounded-full border border-white/10 bg-[rgb(var(--surface-elevated))] px-3 py-2 text-xs text-[rgb(var(--text-secondary))] transition-colors hover:border-[rgb(var(--primary))]/40 hover:text-[rgb(var(--text-primary))]"
                        >
                          {section.title}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Sections */}
                  {content.sections.map((section, idx) => (
                    <div
                      key={idx}
                      id={getSectionAnchorId(guide.id, idx)}
                      className="bg-[rgb(var(--surface))] rounded-2xl p-4 sm:p-5 border border-white/5 scroll-mt-24"
                      data-testid={`post-party-guide-section-${guide.id}-${idx}`}
                    >
                      <div className="flex items-center mb-3">
                        <span className="text-2xl mr-3">{section.icon}</span>
                        <h3 className="text-lg font-bold text-[rgb(var(--text-primary))]">
                          {section.title}
                        </h3>
                      </div>
                      <ul className="space-y-2.5">
                        {section.items.map((item, itemIdx) => (
                          <li key={itemIdx} className="flex items-start text-sm leading-relaxed text-[rgb(var(--text-secondary))]">
                            <span className="text-[rgb(var(--primary))] mr-2 mt-0.5">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  {/* Checklist */}
                  {content.checklist && (
                    <div className="bg-[rgb(var(--surface))] rounded-2xl p-5 border border-white/5" data-testid={`post-party-guide-checklist-${guide.id}`}>
                      <div className="flex items-center mb-3">
                        <ListChecks size={24} weight="duotone" className="text-[rgb(var(--success))] mr-3" />
                        <h3 className="text-lg font-bold text-[rgb(var(--text-primary))]">
                          Quick Recovery Checklist
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {content.checklist.map((item, idx) => (
                          <div key={idx} className="flex items-center text-sm text-[rgb(var(--text-secondary))]">
                            <span className="w-5 h-5 rounded-full border border-[rgb(var(--primary))]/50 mr-2 flex-shrink-0"></span>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="p-6 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center" data-testid={`post-party-guide-footer-${guide.id}`}>
                    <p className="text-[rgb(var(--text-primary))] font-semibold leading-relaxed">
                      {content.footer}
                    </p>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </main>
    </div>
  );
};

export default PostPartySelfCarePage;
